/**
 * Capitalizes the first letter of a string.
 *
 * @param {string} string The string to have its first letter capitalized.
 * @returns {string} The string with the first letter capitalized.
 */
export function capitalizeFirstLetter( string ) {
	if ( ( typeof string ) !== "string" ) {
		return string;
	}
	return string.charAt( 0 ).toUpperCase() + string.slice( 1 );
}

/**
 * The named HTML entities WordPress emits through wptexturize and esc_html.
 *
 * @type {Object<string, string>}
 */
const namedEntities = {
	amp: "&",
	lt: "<",
	gt: ">",
	quot: "\"",
	apos: "'",
	nbsp: "\u00a0",
	hellip: "…",
	ndash: "–",
	mdash: "—",
	lsquo: "‘",
	rsquo: "’",
	ldquo: "“",
	rdquo: "”",
};

/**
 * Decodes the HTML entities in a string.
 *
 * yoast.com returns post titles and descriptions as they are stored in WordPress, which
 * means they contain the entities wptexturize adds (for example `&#8217;` for a curly
 * apostrophe). We render those strings as text, so they need to be decoded first.
 *
 * Decoding happens in a single pass, so an encoded ampersand is not decoded twice:
 * `&amp;#8217;` becomes `&#8217;` and not a curly apostrophe.
 *
 * @param {string} string The string to decode.
 * @returns {string} The decoded string, or the input when it is not a string.
 */
export function decodeHtmlEntities( string ) {
	if ( ( typeof string ) !== "string" ) {
		return string;
	}
	return string.replace( /&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, ( entity, name ) => {
		if ( name.charAt( 0 ) === "#" ) {
			const isHexadecimal = ( name.charAt( 1 ).toLowerCase() === "x" );
			const codePoint = isHexadecimal ? parseInt( name.slice( 2 ), 16 ) : parseInt( name.slice( 1 ), 10 );

			if ( Number.isNaN( codePoint ) || codePoint > 0x10FFFF ) {
				return entity;
			}

			return String.fromCodePoint( codePoint );
		}

		const entityName = name.toLowerCase();

		return Object.hasOwn( namedEntities, entityName ) ? namedEntities[ entityName ] : entity;
	} );
}
